import type {
  PendoConfig,
  pendoIdentify,
  pendoVisitor
} from "@dealer-center/core-interfaces-third-party-pendo";
import { buildMainScript } from "./snippets";
import type {
  PendoIdentifyInternal,
  PendoVisitorInternalAutotrader,
  PendoVisitorInternalDDC
} from "./internalInterface";
import { useState } from "react";

export interface UsePendoInterface {
  initialize: (props: UsePendoInitializeInterface) => boolean;
}
export interface UsePendoInitializeInterface {
  config: PendoConfig;
  identify: pendoIdentify;
}
let isPendoScriptAdded = false;
let isPendoInitialized = false;
export const usePendo: () => UsePendoInterface = () => {
  const [isInitialized, setIsInitialized] = useState(isPendoInitialized);
  const [isScriptAdded, setIsScriptAdded] = useState(isPendoScriptAdded);
  const setIsInitializedInternal = (value: boolean) => {
    isPendoInitialized = value;
    setIsInitialized(value);
  };
  const setScriptAddedInternal = (value: boolean) => {
    isPendoScriptAdded = value;
    setIsScriptAdded(value);
  };
  const initialize = ({ config, identify }: UsePendoInitializeInterface) => {
    if (!config.apiKey) throw new Error("Pendo API key not provided");

    if (!isScriptAdded) {
      const isSuccess = addScript(config.apiKey);
      if (!isSuccess) throw new Error("Failed to add script");
      setScriptAddedInternal(true);
    }

    if (hasScriptAvailable()) {
      if (!isInitialized) {
        window.pendo.initialize(convertToInternal(identify));
        setIsInitializedInternal(true);
      } else {
        window.pendo.identify(convertToInternal(identify));
      }
    } else {
      throw new Error("Pendo script not available");
    }
    return true;
  };
  return {
    initialize,
    clearSession
  };
};
const buildScriptHtml = (apiKey: string) => {
  const script = document.createElement("script");
  script.innerHTML = buildMainScript(apiKey);
  script.setAttribute("data-testid", "pendo-script");
  return script;
};
function addScript(apiKey: string) {
  if (!apiKey) return false;
  const scriptTag = buildScriptHtml(apiKey);
  document.head.appendChild(scriptTag);
  return true;
}

function clearSession() {
  if (typeof window.pendo.clearSession === "undefined") return false;
  window.pendo.clearSession();
  return true;
}

const hasScriptAvailable = () => {
  return typeof window.pendo !== "undefined";
};

// Pendo account ids are namespaced by product so DDC and Autotrader accounts never collide.
// This is the single source of truth for that namespace — deliberately NOT derived from the
// product string itself (e.g. via .toUpperCase()), since the two need not match: "autotrader"
// accounts are namespaced "DDC_AT", not "AUTOTRADER". Record<union, string> makes this exhaustive
// — adding a product to pendoVisitor["product"] without a matching entry here is a type error.
const ACCOUNT_ID_PREFIX: Record<pendoVisitor["product"], string> = {
  ddc: "DDC",
  autotrader: "DDC_AT"
};

const convertToInternal = (data: pendoIdentify): PendoIdentifyInternal => {
  const product = data.visitor.product;
  if (product !== "ddc" && product !== "autotrader") {
    throw new Error("Invalid product");
  }

  const visitor =
    product === "ddc"
      ? convertToDDCVisitor(data)
      : convertToAutotraderVistor(data);

  return {
    visitor,
    account: {
      id: data.account?.accountId
        ? `${ACCOUNT_ID_PREFIX[product]}_${data.account.accountId}`
        : "NO-ACCOUNT-ID",

      name: data.account?.accountName
    }
  };
};

declare global {
  interface Window {
    pendo: {
      initialize: (data: PendoIdentifyInternal) => void;
      identify: (data: PendoIdentifyInternal) => void;
      clearSession: () => void;
    };
  }
}

const convertToDDCVisitor = (data: pendoIdentify): PendoVisitorInternalDDC => {
  return {
    /* eslint-disable @typescript-eslint/naming-convention */
    id: data.visitor.bridgePlatformId,
    bridge_user_name: data.visitor.bridgeUserName,
    first_name: data.visitor.userFirstName,
    username: data.visitor.ddcUserName,
    last_name: data.visitor.userLastName,
    email: data.visitor.userEmail,
    internal: data.visitor.internalUser,
    product: data.visitor.product,
    ddc_dc_permissions: data.visitor.permissions || [],
    ddc_dc_launch_darkly_toggles: data.visitor.launchDarklyToggles
    /* eslint-enable @typescript-eslint/naming-convention */
  };
};

const convertToAutotraderVistor = (
  data: pendoIdentify
): PendoVisitorInternalAutotrader => {
  return {
    /* eslint-disable @typescript-eslint/naming-convention */
    id: data.visitor.bridgePlatformId,
    bridge_user_name: data.visitor.bridgeUserName,
    first_name: data.visitor.userFirstName,
    last_name: data.visitor.userLastName,
    username: data.visitor.ddcUserName,
    email: data.visitor.userEmail,
    internal: data.visitor.internalUser,
    product: data.visitor.product,
    autotrader_dc_launch_darkly_toggles: data.visitor.launchDarklyToggles,
    ...(data.visitor.is_admin !== undefined && {
      is_admin: data.visitor.is_admin
    }),
    ...(data.visitor.is_fraud !== undefined && {
      is_fraud: data.visitor.is_fraud
    }),
    ...(data.visitor.is_helpdesk !== undefined && {
      is_helpdesk: data.visitor.is_helpdesk
    }),
    ...(data.visitor.is_syc !== undefined && { is_syc: data.visitor.is_syc }),
    ...(data.visitor.is_eas !== undefined && { is_eas: data.visitor.is_eas }),
    ...(data.visitor.is_priv !== undefined && { is_priv: data.visitor.is_priv })
    /* eslint-enable @typescript-eslint/naming-convention */
  };
};
